<template>
  <layout-private  :customer="customer">

      <div class="row">
        <div v-if="!customer || !customer.group_count || !customer.location_count" class="text-center col-title col-12 col-lg-6 offset-lg-3 col-xl-6 offset-xl-3">
          <h4 class="light-weight snug">Getting Started: Step 2 of 5</h4>
          <h1>Add Groups &amp; Locations</h1>
        </div> <!-- /.col-x -->
        <div v-if="!customer || !customer.group_count || !customer.location_count" class="text-center col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
          <p>Add groups and locations for your account. Each location and group will have its own activity feed and stats. You'll assign team members to these later. Remember, each location will need an Open Dental connection too.</p>
          <p class="sm"><em>Note: <strong>groups and locations cannot be deleted</strong> after you save them.</em></p>
          <p v-if="!customer"><i class="far fa-fw fa-pulse fa-spinner"></i></p>
        </div> <!-- /.col-x -->
        <div v-if="customer && customer.group_count && customer.location_count" class="col-12 col-lg-6">
          <h1 class="title-short">Groups &amp; Locations</h1>
        </div> <!-- /.col-x -->
        <div v-if="customer && customer.group_count && customer.location_count" class="col-12 col-lg-6 text-lg-right">
          <p class="snug sm light-weight mt-2"><em>Note: groups &amp; locations cannot be deleted after you've saved them.</em></p>
        </div> <!-- /.col-x -->

        <div class="col-12">
          <hr />
        </div>

        <div v-if="customer" v-for="(group, indexGroup) in groups" class="col-group col-12 col-md-6 col-lg-4">
          <h3>
            Group {{indexGroup + 1}}
            <button v-if="indexGroup !== 0 && (!oldGroups.length || !group.id || !oldGroups.some(oldGroup => oldGroup.id === group.id) )" class="btn btn-remove" @click="removeGroup(indexGroup)"><i class="far fa-times"></i></button>
            <button
                class="btn btn-hide btn-icon-only btn-no-animation"
                v-if="group.id && oldGroups.some(oldGroup => oldGroup.id == group.id)"
                @click="hideGroup(indexGroup)"
              >
                <i v-if="!group.hidden && !group.savingHiddenState" class="far fa-eye fa-tooltip"><span class="tip">
                  This group is visible. Click to hide it and its locations in Smiley Mobile.
                </span></i>
                <i v-if="group.hidden && !group.savingHiddenState" class="far fa-eye-slash fa-tooltip "><span class="tip">
                  This group and its locations are hidden. Click to show it and its locations in Smiley Mobile.
                </span></i>
                <i v-if="group.savingHiddenState" class="far fa-fw fa-pulse fa-spinner"></i>
              </button>
          </h3>
          <input v-model="group.groupName" name="groupName" class="form-control" placeholder="Enter Group Name" type="text" />
          <div v-for="(location, indexLocation) in group.locations" class="form-group">
            <label>Location {{ indexLocation + 1 }}</label>
            <div class="input-group input-group-append">
              <input v-model="location.locationName" name="locationName" class="form-control" placeholder="Enter Location Name" type="text" />
              <button
                v-if="indexLocation !== 0 && (!oldGroups.length || !location.id || !oldGroups.some(oldGroup => oldGroup.locations.some(oldLocation => oldLocation.id === location.id)) )"
                class="btn btn-remove"
                @click="removeLocation(indexGroup,indexLocation)"
              >
                <i class="far fa-times"></i>
              </button>
              <button
                class="btn btn-hide btn-icon-only btn-no-animation"
                v-if="!group.hidden && location.id && oldGroups.some(oldGroup => oldGroup.locations.some(oldLocation => oldLocation.id === location.id))"
                @click="hideLocation(indexLocation, indexGroup)"
              >
                <i v-if="!location.hidden && !location.savingHiddenState" class="far fa-eye fa-tooltip"><span class="tip">
                  This location is visible. Click to hide in Smiley Mobile.
                </span></i>
                <i v-if="location.hidden && !location.savingHiddenState" class="far fa-eye-slash fa-tooltip "><span class="tip">
                  This location is hidden. Click to show in Smiley Mobile.
                </span></i>
                <i v-if="location.savingHiddenState" class="far fa-fw fa-pulse fa-spinner"></i>
              </button>
              <button
                class="btn btn-hide btn-icon-only btn-no-animation"
                disabled
                v-if="location.hidden && group.hidden"
              ><i class="far fa-eye-slash"></i></button>
              
            </div> <!-- /.input-group -->
          </div>
          <div class="form-group" v-if="customer.location_limit == 'unlimited' || group.locations.length < customer.location_limit">
            <button
              @click.prevent="addLocation(indexGroup)"
              class="btn btn-link"
            >
              <i class="far fa-plus-circle"></i>Add Location
            </button>
          </div>
        </div> <!-- /.col-x -->

        <div
          v-if="customer && (groups.length < customer.group_limit || customer.group_limit == 'unlimited')"
          v-for="i in (groups.length > 1 ? 1 : 2 )"
          class="col-add-group col-12 col-md-6 col-lg-4"
        >
          <div class="box d-flex justify-content-center align-items-center">
            <button @click.prevent="addGroup" class="btn btn-link"><i class="far fa-plus-circle"></i>Add Group</button>
          </div>
        </div>

        <div v-if="customer" class="col-footer col-12 text-center">
          <p v-if="customer.group_limit && customer.location_limit">
            <strong>Your plan limits:</strong> <span class="badge badge-custom">Groups: <em>{{ customer.group_limit }}</em></span> <span class="badge badge-custom">Locations per Group: <em>{{ customer.location_limit }}</em></span>
          </p>
          <p v-if="customer.group_count && customer.location_count">
            <strong>Your plan totals:</strong> <span class="badge badge-custom">Groups: <em>{{ customer.group_count }}</em></span> <span class="badge badge-custom"> Most Locations per Group: <em>{{ customer.location_count }}</em></span>
          </p>
          <hr />
          <list-errors :errors="errors"></list-errors>
          <p v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i></p>
          <p v-if="groups.some(group => group.hidden !== null && group.hidden !== undefined) || groups.some(group => group.locations.some(location => location.hidden !== null && location.hidden !== undefined))" class="instructions"><small><em>Note: When hiding or showing a group or location, changes are automatically saved.</em></small></p>
          <p v-if="success"><i class="far fa-fw fa-check green-color"></i> Updates Saved Successfully. Need to <router-link to="/open-dental">connect new locations to Open Dental</router-link>?</p>
          <p class="snug"><button v-if="!loading" @click.prevent="saveGroups" class="btn btn-app">
            <span v-if="!customer.group_count || !customer.location_count">Save &amp; Continue<i class="far fa-angle-right fa-fw"></i></span>
            <span v-if="customer.group_count && customer.location_count">Save Updates</span>
          </button></p>
        </div>

      </div> <!-- /.row -->
  </layout-private>
</template>

<script>
import Vue from 'vue'
import LayoutPrivate from '../components/LayoutPrivate.vue'
import {mapGetters} from 'vuex'
import ListErrors from '../components/ListErrors.vue'

export default {
  props: ['customer'],
  components: {
    LayoutPrivate, ListErrors,
  },
  data () {
    return {
      errors: [],
      loading: false,
      success: false,
      groups: [],
      oldGroups: [],
    }
  },
  computed: {
    ...mapGetters(['user', 'user_props', 'custom_claims']),
  },
  watch: {
    customer() {
      // don't load if groups already exist
        // could have just saved them and be waiting on redirect.
        // cust object updates when new groups are saved
      if(this.customer && !this.groups.length) {
        this.loadGroupsLocations();
      }
    },
    // check for redirect on loading custom claims
    custom_claims() {
      if(!this.custom_claims || !this.custom_claims.adminLevel || this.custom_claims.adminLevel !== "superAdmin") {
        this.$router.replace('/')
      }
    },
  },
  methods: {
    addGroup() {
      // check if group limit reached
      // check if location limit reached
      if(this.customer.group_limit == "unlimited" || this.groups.length < this.customer.group_limit) {
        this.groups.push({
          groupName: null,
          locations: [{
            locationName: null,
          }]
        });
      }
    },
    addLocation(groupIndex) {
      // console.log(`addLocation to group ${groupIndex}`)
      // check if valid group index
      if(Number.isInteger(groupIndex) && this.groups[groupIndex]) {
        // check if location limit reached
        if(this.customer.location_limit == "unlimited" || this.groups[groupIndex].locations.length < this.customer.location_limit) {
          this.groups[groupIndex].locations.push({locationName: null})
        }
      }
    },
    removeLocation(indexGroup,indexLocation) {
      // console.log({indexGroup,indexLocation})
      // console.log(this.groups[indexGroup].locations[indexLocation] || "false")
      if(indexLocation !== 0 && this.groups[indexGroup] && this.groups[indexGroup].locations[indexLocation]) {
        this.groups[indexGroup].locations.splice(indexLocation,1);
      }
    },
    removeGroup(indexGroup) {
      if(indexGroup !== 0 && this.groups[indexGroup]) {
        this.groups.splice(indexGroup,1);
      }
    },
    hideLocation(locationIndex,groupIndex) {
      if(this.groups[groupIndex] && this.groups[groupIndex].locations[locationIndex]) {
        let group = this.groups[groupIndex];
        let location = this.groups[groupIndex].locations[locationIndex];
        let hiddenStatus;
        // if hidden, unhide it
        if(location.hidden) {
          hiddenStatus = false;
        }
        // if not hidden, hide it
        if(!location.hidden) {
          hiddenStatus = true;
        }
        // save updates
        this.$set(this.groups[groupIndex].locations, locationIndex, {
          ...location,
          savingHiddenState: true,
          hidden: hiddenStatus,
        });
        Vue.firestore.doc(`customers/${this.customer.id}/groups/${group.id}/locations/${location.id}`)
          .update({
            hidden: hiddenStatus,
          })
          .then(() => {
            this.$set(this.groups[groupIndex].locations, locationIndex, {
              ...location,
              savingHiddenState: false,
              hidden: hiddenStatus,
            });
          })
          .catch(err => console.log(err));
      }
    },
    hideGroup(groupIndex) {
      if(this.groups[groupIndex]) {
        let group = this.groups[groupIndex];
        let hiddenStatus;
        // if hidden, unhide it
        if(group.hidden) {
          hiddenStatus = false;
        }
        // if not hidden, hide it
        if(!group.hidden) {
          hiddenStatus = true;
        }
        // save updates
        this.$set(this.groups, groupIndex, {
          ...group,
          savingHiddenState: true,
          hidden: hiddenStatus,
        });
        // open batch changes
        var batch = Vue.firestore.batch();
        let groupRef = Vue.firestore.doc(`customers/${this.customer.id}/groups/${group.id}`);
        batch.update(groupRef, {
          hidden: hiddenStatus,
        });
        // loop through group's locations and save batch updates to set them hidden/unhidden
        group.locations.forEach((loc, locIndex) => {
          let locRef = Vue.firestore.doc(`customers/${this.customer.id}/groups/${group.id}/locations/${loc.id}`);
          batch.update(locRef, {
            hidden: hiddenStatus,
          });
          this.$set(this.groups[groupIndex].locations, locIndex, {
            ...loc,
            hidden: hiddenStatus,
          });
        });
        // commit all changes
        batch.commit()
          .then(() => {
            this.$set(this.groups, groupIndex, {
              ...group,
              savingHiddenState: false,
              hidden: hiddenStatus,
            });
          })
          .catch(err => console.log(err));
      }
    },
    async loadGroupsLocations() {
      this.errors = [];
      if(this.customer && !this.oldGroups.length) {
        try {
          this.loading = true;
          let groupsQuerySnap = await Vue.firestore.collection('customers')
            .doc(this.customer.id)
            .collection('groups')
            .get();

          // console.log({ groupsQuerySnap });
          if(groupsQuerySnap.empty) {
            console.log("couldn't find location groups in your account.")
            // if none found, set a blank group
            this.groups.push({
              groupName: null,
              locations: [{
                locationName: null,
              }],
            });
            this.loading = false;
            return true;
          }
          for(const group of groupsQuerySnap.docs) {
            // console.log(group.data())
            let groupData = {
              ...group.data(),
              id: group.id,
              locations: [],
            }
            // build completely separate objects to compare changes against
              // must be new objects or they will be reactive and comparisons
              // will fail
            let oldGroupData = {
              ...group.data(),
              id: group.id,
              locations: [],
            }
            // onSnapShot is not async so cannnot await it
            group.ref.collection('locations').get().then(locationQuerySnap => {
              // console.log({ locationQuerySnap });
              if(locationQuerySnap.empty) {
                throw new Error("Error: couldn't find locations in your account.")
              }
              for(const [i,location] of locationQuerySnap.docs.entries()) {
                // console.log(location.data(), location.id);
                // store locations with id
                groupData.locations.push({
                  ...location.data(),
                  id: location.id,
                });
                // redundant but necessary
                oldGroupData.locations.push({
                  ...location.data(),
                  id: location.id,
                });
              }
              // console.log({groupData});
              this.groups.push(groupData);
              this.oldGroups.push(oldGroupData);
              this.loading = false;
            });
          }
        } catch(err) {
          this.errors.push(err.message)
          console.log(err);
        }
      }
    },
    async saveGroups() {
      // reset errors
      this.errors = [];
      this.loading = true;
      // validate logged in
      if(!this.user) {
        this.errors.push('You must be logged in. Please refresh the page.')
      }
      // validate all groups and locations have names
      if(this.groups.some(group => !group.groupName) || this.groups.some(group => group.locations.some(location => !location.locationName))) {
        this.errors.push('All groups and locations must have a name.')
      }
      // validate name length
      if(this.groups.some(group => group.groupName.length <= 3) || this.groups.some(group => group.locations.some(location => !location.locationName || location.locationName.length <= 3))) {
        this.errors.push('All group and location names must be longer than 3 characters.')
      }
      // validate if names are only spaces
      if(this.groups.some( group => !group.groupName.trim().length) || this.groups.some(group => group.locations.some(location => location.locationName && !location.locationName.trim().length)) ) {
        this.errors.push('Group and location names cannot be only spaces.')
      }
      // validate not over plan limits
      if(this.customer.group_limit !== "unlimited" && this.groups.length > this.customer.group_limit) {
        this.errors.push('You cannot save more groups than the limit on your subscription plan.')
      }
      if( this.customer.location_limit !== "unlimited" && this.groups.some(group => group.locations.length > this.customer.location_limit) ) {
        this.errors.push('You cannot save more locations than the limit on your subscription plan.')
      }
      // stop if errors
      if(this.errors.length) {
        this.loading = false;
        return;
      }

      var batch = Vue.firestore.batch();
      let groupCount = this.groups.length;
      let locationCount = 0;

      // set defaults if we already have counts
      if(this.customer.location_count) {
        locationCount = this.customer.location_count;
      }
      if(this.customer.group_count) {
        groupCount = this.groups.length;
      }

      this.groups.forEach( async (group) => {
        // set locationCount to the largest group
          // should not be the sum of all locations but the most locations
          // within any group
        if(group.locations.length > locationCount) {
          locationCount = group.locations.length;
        }
        let ref;
        // if a new group
        if(!this.oldGroups.length || !this.oldGroups.some(oldGroup => oldGroup.id == group.id)) {
          // console.log('saving new group');
          ref = Vue.firestore.collection('customers')
            .doc(this.customer.id)
            .collection('groups')
            .doc(); // sets auto-generated id since batch.add() is not valid
          batch.set(ref, {
            groupName: group.groupName,
            customerId: this.customer.id,
          });
        }
        // else if an old group
          // save ref even if it didn't change so we can add locations to it
         else if(this.oldGroups.length && this.oldGroups.some(oldGroup => oldGroup.id == group.id)) {
          // console.log('updating old group');
          ref = Vue.firestore.doc(`customers/${this.customer.id}/groups/${group.id}`);
          // save update if the group name changed
          if(this.oldGroups.some(oldGroup => oldGroup.id == group.id && oldGroup.groupName !== group.groupName )) {
            batch.update(ref, {
              groupName: group.groupName,
            });
          }
        }
        // store each location with its own id in a subcollection
        group.locations.forEach((location) => {
          let locRef;
          // if new location
          if(!this.oldGroups.length || !this.oldGroups.some(oldGroup => oldGroup.locations.some(oldLocation => oldLocation.id == location.id))) {
            // console.log('saving new location');
            locRef = ref.collection('locations').doc();
            batch.set(locRef, {
              ...location,
              customerId: this.customer.id,
              groupId: ref.id,
              user_count: 0,
              id: locRef.id,
            });
          }

          // if old location with changed name
          if(this.oldGroups.length && this.oldGroups.some(oldGroup => oldGroup.locations.some(oldLocation => oldLocation.id == location.id && oldLocation.locationName !== location.locationName))) {
            // console.log('updating old location');
            locRef = ref.collection('locations').doc(location.id);
            batch.update(locRef, {
              locationName: location.locationName,
            });
          }
        });
      })
      // save counters
      if(!this.customer.group_count || !this.customer.location_count || this.customer.location_count < locationCount || this.customer.group_count < groupCount) {
        let custRef = Vue.firestore
          .collection('customers').doc(this.customer.id);
        batch.update(custRef, {
          group_count: groupCount,
          location_count: locationCount,
        })
      }

      // commit all
      // console.log({batch});
      batch.commit()
      .then(() => {
        // console.log(`groups saved, old groups length: ${this.oldGroups.length}`)
        if(this.oldGroups.length) {
          this.oldGroups = [];
          this.groups = [];
          this.loadGroupsLocations();
          this.success = true;
        } else {
          this.$router.push('/open-dental')
        }
        this.loading = false;
      })
      .catch((err) => {
        this.errors.push(`An error occured: ${err.message}`);
        console.log(err);
        this.loading = false;
      });
    },
  },
  mounted () {
    // this.checkCustomer(); // if check here will always redirect away on page refresh
    this.loadGroupsLocations();
  },
}
</script>

<style lang="less" scoped>
@import "../assets/less/variables.less";

.col-group,
.col-add-group {
  margin-bottom: 50px;
}
// styles to inset location
.col-group .form-group {
  padding: 1em 0 0 40px;
  position: relative;

  &:before {
    content: '';
    display: block;
    width: 30px;
    height: 30px;
    border-left: 1px solid @color-blue;
    border-bottom: 1px solid @color-blue;
    position: absolute;
    top: 0;
    left: 0;
  }
}
.col-add-group {
  min-height: 285px;

  .box {
    width: 100%;
    height: 100%;
    border: 1px solid @color-blue;
  }
}
.col-footer {
  padding-bottom: 50px;
}
</style>
